import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  DatePicker,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Spin,
  Steps,
  Table,
  Tag,
  Tooltip,
  message,
} from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import Swal from "sweetalert2";
import { Button, Icon } from "../../../../components/Component";
import {
  equipmentRateApis,
  EquipmentRateFields,
  EquipmentRateHeads,
  EquipmentRateIntermediates,
  CrewBreakdownLine,
} from "../../../../api/MachineryEquipment";
import { statusTagColor, statusLabel } from "../../assets/AssetStatusModal";
import adminOrgApis from "../../../../api/master/adminOrg";
import RateUnitToggle from "../../shared/RateUnitToggle";
import { moneyPerUnit, useRateUnit } from "../../shared/useRateUnit";
import RateParametersDrawer from "./RateParametersDrawer";
import "./EquipmentRateEditor.css";

/**
 * Equipment Hourly Use Rate editor — a faithful mirror of the source workbook,
 * now EFFECTIVE-DATED.
 *
 * The machine's IDENTITY (name, code, capacity) lives on the Asset Master and
 * is read-only here; this form edits the ~24 engineering inputs of a RATE
 * VERSION. Saving never overwrites: it INSERTS a new version effective from a
 * chosen instant (default now; back-dating recomputes history by design). The
 * History drawer shows the full timeline, each version priced by the parameter
 * card that was in force at its own instant; only the latest version can be
 * removed (undo).
 *
 * The sheet ("Hourly Use Rate of Equipment.xlsx") runs every machine through
 * SIX NUMBERED CHARGE SECTIONS, each closing with its own "Hourly Cost" line,
 * then a grand total. This form reproduces that anatomy as a LEFT-RAIL
 * STEPPER: the rail lists the sections with each one's computed Hourly Cost,
 * the middle shows one section at a time, and the sticky Summary panel on the
 * right is the workbook's separate recap block (its rows 111-122).
 *
 * Navigation is FREE — any step is clickable in any order; Back/Next exist
 * purely for a guided first pass.
 *
 * EVERY DERIVED NUMBER COMES FROM THE SERVER. The Recalculate button calls
 * PreviewEquipmentRate; nothing here re-implements a formula, and nothing fires
 * per keystroke.
 *
 * PERCENTAGES are whole numbers in this form and fractions in the API. The
 * conversion happens only in toFields/fromApi below.
 */

interface FieldSpec {
  key: keyof EquipmentRateFields;
  label: string;
  /** stored as a fraction, edited as percent */
  percent?: boolean;
  suffix?: string;
  hint?: string;
  /** can inherit its value from the rate-parameters card when left blank */
  inheritable?: boolean;
}

// ---- section input specs, in workbook order --------------------------------

const OWNERSHIP_FIELDS: FieldSpec[] = [
  { key: "costOfEquipmentLacs", label: "Cost of Equipment", suffix: "Lacs" },
  { key: "scrapValuePct", label: "Scrap Value", percent: true, suffix: "%", inheritable: true },
  { key: "lifeHours", label: "Life", suffix: "hours" },
  { key: "lifeYears", label: "Life", suffix: "years" },
];

/**
 * The ₹/unit denominator. Its own spec (not in OWNERSHIP_FIELDS) because the
 * section renders a UOM <Select> beside it, which the generic numeric field
 * renderer knows nothing about.
 */
const OUTPUT_FIELD: FieldSpec = {
  key: "standardOutputPerHour",
  label: "Standard Output",
  suffix: "per hour",
  hint: "Optional. The machine's nominal output rate, e.g. 45 meaning 45 Cum/Hr. It is the denominator behind the ₹ per unit view. Equipment Norms use their own production norm instead.",
};

const INSURANCE_FIELDS: FieldSpec[] = [
  { key: "insurancePctPerYear", label: "Insurance Charges per Year", percent: true, suffix: "% of cost" },
];

const INTEREST_FIELDS: FieldSpec[] = [
  { key: "interestRatePct", label: "Rate of Interest", percent: true, suffix: "% pa", inheritable: true },
];

const REPAIR_FIELDS: FieldSpec[] = [
  { key: "repairPctOfCost", label: "Repair & Maintenance", percent: true, suffix: "% of cost", hint: "Over the machine's whole life. Commonly 20–150%." },
];

const DIESEL_FIELDS: FieldSpec[] = [
  { key: "bhpRating", label: "BHP Rating", suffix: "HP" },
  { key: "dieselC1", label: "Diesel C1", hint: "Plain multiplier, not a percentage" },
  { key: "dieselC2", label: "Diesel C2", hint: "Plain multiplier, not a percentage" },
  { key: "dieselEffectiveUsagePct", label: "Effective Usage", percent: true, suffix: "%" },
  { key: "lubricantPctOfFuel", label: "Lubricant", percent: true, suffix: "% of fuel" },
];

const ELECTRIC_FIELDS: FieldSpec[] = [
  { key: "totalPowerKw", label: "Total Power", suffix: "kW" },
  { key: "elecC1", label: "Electricity C1", hint: "Plain multiplier, not a percentage" },
  { key: "elecC2", label: "Electricity C2", hint: "Plain multiplier, not a percentage" },
  { key: "elecEffectiveUsagePct", label: "Effective Usage", percent: true, suffix: "%" },
  { key: "lubricantPctOfElectricity", label: "Lubricant", percent: true, suffix: "% of power" },
];

// In the workbook Sundries lives INSIDE the POL section (its rows 59-60 feed the
// Total POL row) — not under ownership. Mirrored here.
const SUNDRIES_FIELDS: FieldSpec[] = [
  { key: "sundriesPctOfRepair", label: "Sundries & Misc", percent: true, suffix: "% of repair", inheritable: true },
];

const CREW_ENVELOPE_FIELDS: FieldSpec[] = [
  { key: "noOfShifts", label: "No. of Shifts", suffix: "nos" },
];

const INDIRECT_FIELDS: FieldSpec[] = [
  { key: "indirectSkilledPct", label: "Indirect — Skilled", percent: true, suffix: "%" },
  { key: "indirectUnskilledPct", label: "Indirect — Unskilled", percent: true, suffix: "%" },
];

/** §6 table rows. Mirrors the backend MACHINERY_CREW_ROLES order and split. */
const CREW_ROLES: Array<{
  field: keyof EquipmentRateFields;
  roleKey: string;
  label: string;
  skill: "Skilled" | "Unskilled";
}> = [
  { field: "crewForemanNos", roleKey: "FOREMAN", label: "Foreman", skill: "Skilled" },
  { field: "crewOperatorNos", roleKey: "OPERATOR", label: "Operator", skill: "Skilled" },
  { field: "crewMechanicNos", roleKey: "MECHANIC", label: "Mechanic", skill: "Skilled" },
  { field: "crewElectricianNos", roleKey: "ELECTRICIAN", label: "Electrician", skill: "Skilled" },
  { field: "crewSupervisorNos", roleKey: "SUPERVISOR", label: "Supervisor", skill: "Skilled" },
  { field: "crewDriverNos", roleKey: "DRIVER", label: "Driver", skill: "Skilled" },
  { field: "crewCablemanNos", roleKey: "CABLEMAN", label: "Cableman", skill: "Skilled" },
  { field: "crewHelperNos", roleKey: "HELPER", label: "Helper", skill: "Unskilled" },
  { field: "crewWatchmanNos", roleKey: "WATCHMAN", label: "Watchman", skill: "Unskilled" },
  { field: "crewBeldarNos", roleKey: "BELDAR", label: "Beldar", skill: "Unskilled" },
];

const ALL_NUMERIC_FIELDS: FieldSpec[] = [
  ...OWNERSHIP_FIELDS,
  // NOTE: outputUomId is deliberately NOT here — this list drives the numeric
  // fromApi/toFields coercion, and an id must not go through the percent branch
  // or be re-typed by Number() on the way to a Select.
  OUTPUT_FIELD,
  ...INSURANCE_FIELDS,
  ...INTEREST_FIELDS,
  ...REPAIR_FIELDS,
  ...DIESEL_FIELDS,
  ...ELECTRIC_FIELDS,
  ...SUNDRIES_FIELDS,
  ...CREW_ENVELOPE_FIELDS,
  ...INDIRECT_FIELDS,
  ...CREW_ROLES.map(
    (role) => ({ key: role.field, label: role.label, suffix: "nos/shift" }) as FieldSpec,
  ),
];

// The Summary mirrors the left rail's six sections IN ORDER — the panel is the
// rail with its numbers filled in. POL is one line (the section total) with its
// four constituents indented beneath it, so 1+2+3+4+5+6 visibly equals Total.

/**
 * How each head is computed — the SAME wording as the export's Calculation
 * Notes sheet, which was verified cell-by-cell against the source workbook.
 * DISPLAY ONLY: the server computes everything; these tooltips just explain it.
 */
const HEAD_FORMULAS: Record<string, string> = {
  annualHours: "Annual Hours = Life (Hours) ÷ Life (Years)",
  depreciation:
    "Depreciation = Cost × (1 − Scrap %) ÷ Life (Years) ÷ Annual Hours",
  insurance: "Insurance = Cost × Insurance % per year ÷ Annual Hours",
  interest:
    "Interest = Cost × (n+1) ÷ 2n × Interest % ÷ Annual Hours, where n = Life (Years) — the average-annual-investment method",
  repair: "Repair = Cost × Repair % ÷ Life (Hours) — spread over life hours, not annual hours",
  misc: "Misc = Repair × Sundries %",
  fuel: "Fuel = 0.22 × BHP × C1 × C2 × Effective Usage % × diesel price (from the rate-parameters card)",
  electricity:
    "Electricity = kW × C1 × C2 × Effective Usage % × electricity rate (from the rate-parameters card)",
  lubricants:
    "Lubricants = Fuel × Lubricant % of Fuel + Electricity × Lubricant % of Electricity — both on the EFFECTIVE (actual) energy cost. The source workbook applies the % to the gross cost before effective usage, so its figure reads higher for machines running below 100% usage; ours is the deliberate correction.",
  crew: "Crew = Σ per role: Shifts × monthly wage × Nos/Shift, split skilled/unskilled, × 12 ÷ Annual Hours, plus the Indirect %",
  totalPol:
    "Total POL Cost = Sub Total Fuel & Lubs + Sub Total Power & Lubs + Sundries & Misc — the workbook's rows 5.12 + 5.13 + 5.15 = 5.16",
  total: "Total = the nine heads summed, each rounded to 4 decimals first so the head-sum equals the total exactly",
};

// ---- formatting ------------------------------------------------------------

const money = (value: any): string => {
  if (value === undefined || value === null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/** Quantities like litres/hr — up to 4 dp, trailing zeros trimmed. */
const qty = (value: any): string => {
  if (value === undefined || value === null || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return String(Math.round(n * 1e4) / 1e4);
};

const WARNING_LABELS: Record<string, string> = {
  MISSING_LIFE_DATA: "Life hours and years are needed for the ownership costs",
  RATE_PARAMETERS_MISSING: "No rate parameters set up for this scope",
  MISSING_COST: "No equipment cost entered",
};
const warningLabel = (code: string) =>
  WARNING_LABELS[code] ??
  (code.startsWith("WAGE_MISSING:")
    ? `No wage recorded for ${code.split(":")[1]?.toLowerCase()}`
    : code);

type FormState = Record<string, any>;

// ---- component -------------------------------------------------------------

const EquipmentRateEditor = ({
  assetId,
  projectId = 0,
  projectName = "",
  onClose,
  onSaved,
}: {
  /** The machine, from the Asset Master. Machines are CREATED there, not here. */
  assetId: number;
  projectId?: number;
  /** Display name for the scope tag — "Trumpet HEP", never a raw id. */
  projectName?: string;
  onClose: () => void;
  onSaved: () => void;
}) => {
  const [asset, setAsset] = useState<any>(null);
  /** The version whose inputs are in the form (null = no rate yet). */
  const [baseVersion, setBaseVersion] = useState<any>(null);
  const [versionCount, setVersionCount] = useState(0);
  const [form, setForm] = useState<FormState>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [heads, setHeads] = useState<EquipmentRateHeads | null>(null);
  const [intermediates, setIntermediates] = useState<EquipmentRateIntermediates | null>(null);
  const [crewByRole, setCrewByRole] = useState<Map<string, CrewBreakdownLine>>(new Map());
  const [inheritedFields, setInheritedFields] = useState<string[]>([]);
  const [previewing, setPreviewing] = useState(false);
  const [dirtySincePreview, setDirtySincePreview] = useState(false);
  const [parametersOpen, setParametersOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [uomOptions, setUomOptions] = useState<any[]>([]);

  const [rateUnit] = useRateUnit();
  /**
   * The per-unit view is only meaningful once the draft HAS a standard output.
   * heads.perUnit is the server's answer to that — it is absent whenever the
   * denominator is missing, so this single check covers both "never set" and
   * "just cleared, not yet recalculated".
   */
  const showPerUnit = rateUnit === "OUTPUT" && Boolean(heads?.perUnit);
  // Server label first; the option list is the fallback before a Recalculate.
  // Options read "Cum (CUM)" — strip the short code, a unit suffix wants "Cum".
  const outputUomLabel =
    heads?.perUnit?.uomName ||
    String(
      uomOptions.find((opt) => Number(opt.value) === Number(form.outputUomId))
        ?.label || ""
    ).split(" (")[0];

  /** The active-unit value for one summary row. */
  const summaryValue = (key: string, hourly: any) =>
    showPerUnit ? (heads?.perUnit as any)?.[key] : hourly;

  /** Tooltip text for the active unit — the ₹/hr formula plus the divisor. */
  const summaryFormula = (key: string) =>
    showPerUnit
      ? `${HEAD_FORMULAS[key]}\n\nThen ÷ Standard Output per Hour to give ₹ per ${
          outputUomLabel || "unit"
        }.`
      : HEAD_FORMULAS[key];

  /**
   * POL per unit. It is the workbook's SUBTOTAL of four heads rather than an
   * engine head, so the server sends no perUnit sibling for it — rebuild it
   * from the four that do exist, which is the same arithmetic the hourly
   * totalPol represents.
   */
  const polPerUnit = useMemo(() => {
    const perUnit: any = heads?.perUnit;
    if (!perUnit) return undefined;
    const parts = ["fuel", "electricity", "lubricants", "misc"]
      .map((key) => perUnit[key])
      .filter((value) => value !== undefined && value !== null);
    if (!parts.length) return undefined;
    return parts.reduce((sum: number, value: any) => sum + Number(value), 0);
  }, [heads]);

  /** Rail description value for a step, in the per-unit basis. */
  const railPerUnit = (headKey?: string) => {
    if (!headKey) return undefined;
    if (headKey === "totalPol") return polPerUnit;
    return (heads?.perUnit as any)?.[headKey];
  };

  // The save-as-version modal: WHEN the new inputs take effect, and why.
  const [saveOpen, setSaveOpen] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState<Dayjs>(dayjs());
  const [remarks, setRemarks] = useState("");

  // Rate History drawer.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // The card has no internal scroll (it grows with its content), so on a step
  // change just make sure its top is in view — 'nearest' is a no-op when it
  // already is, and a minimal page scroll when the user was deep in a long
  // section.
  const stepCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    stepCardRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [activeStep]);

  const applyComputed = useCallback(
    (source: {
      heads?: EquipmentRateHeads;
      intermediates?: EquipmentRateIntermediates;
      crewBreakdown?: CrewBreakdownLine[];
      inheritedFields?: string[];
    }) => {
      setHeads(source.heads ?? null);
      setIntermediates(source.intermediates ?? null);
      setCrewByRole(new Map((source.crewBreakdown ?? []).map((line) => [line.key, line])));
      setInheritedFields(source.inheritedFields ?? []);
      setDirtySincePreview(false);
    },
    [],
  );

  /** API fractions -> form percents. */
  const fromApi = useCallback((version: any): FormState => {
    const next: FormState = {};
    for (const field of ALL_NUMERIC_FIELDS) {
      const raw = version?.[field.key];
      if (raw === null || raw === undefined || raw === "") {
        next[field.key as string] = null;
        continue;
      }
      const n = Number(raw);
      next[field.key as string] = field.percent ? Math.round(n * 100 * 1e6) / 1e6 : n;
    }
    // An id, not a measurement — kept out of the loop above so the percent
    // branch can never touch it.
    next.outputUomId = version?.outputUomId ? Number(version.outputUomId) : null;
    return next;
  }, []);

  /** Form percents -> API fractions. Numeric inputs only — identity is the asset's. */
  const toFields = useCallback((): EquipmentRateFields => {
    const fields: EquipmentRateFields = {};
    for (const field of ALL_NUMERIC_FIELDS) {
      const value = form[field.key as string];
      if (value === null || value === undefined || value === "") continue;
      (fields as any)[field.key] = field.percent ? Number(value) / 100 : Number(value);
    }
    if (form.outputUomId) fields.outputUomId = Number(form.outputUomId);
    return fields;
  }, [form]);

  const load = useCallback(async () => {
    setActiveStep(0);
    setLoading(true);
    try {
      const [res, uomRes] = await Promise.all([
        equipmentRateApis.getAssetRate(assetId),
        adminOrgApis.fetchUom(),
      ]);
      // Same mapping as the Deployment Norm editor: value workUomId??uomId??id.
      const uomData = uomRes?.data;
      if (uomRes?.status && Array.isArray(uomData)) {
        setUomOptions(
          uomData.map((item: any) => ({
            value: Number(item?.workUomId ?? item?.uomId ?? item?.id),
            label: item?.uomShortCode
              ? `${item?.uomName} (${item?.uomShortCode})`
              : item?.uomName,
          }))
        );
      }
      const data = res?.data;
      if (data?.asset) {
        setAsset(data.asset);
        setBaseVersion(data.version ?? null);
        setVersionCount(Number(data.versionCount) || 0);
        setForm(fromApi(data.version ?? null));
        // Merge the version's own UOM when the org list doesn't offer it: the
        // backend may hold the GLOBAL uom_master row (e.g. "Cubit Meter" id 3)
        // while /admin/fetchUom returns the org-scoped list ("Cum" id 213). Same
        // workaround as the Deployment Norm editor, and without it the select
        // renders a bare id.
        if (data.version?.outputUomId && data.version?.outputUomName) {
          setUomOptions((prev) =>
            prev.some(
              (opt) => Number(opt.value) === Number(data.version.outputUomId)
            )
              ? prev
              : [
                  ...prev,
                  {
                    value: Number(data.version.outputUomId),
                    label: String(data.version.outputUomName),
                  },
                ]
          );
        }
        if (data.version) {
          // fetch-one carries the full detailed computation, so the sections
          // render immediately, before any Recalculate.
          applyComputed(data);
        } else {
          applyComputed({});
          setDirtySincePreview(true); // nothing computed yet — invite a Recalculate
        }
      } else {
        message.error("Could not load this machine.");
      }
    } finally {
      setLoading(false);
    }
  }, [assetId, fromApi, applyComputed]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setDirtySincePreview(true);
  };

  const handleRecalculate = async () => {
    setPreviewing(true);
    try {
      const res = await equipmentRateApis.preview({ ...toFields(), projectId });
      if (res?.status) {
        applyComputed(res.data ?? {});
      } else {
        message.error(res?.data?.message || "Could not calculate.");
      }
    } finally {
      setPreviewing(false);
    }
  };

  const openSaveModal = () => {
    setEffectiveFrom(dayjs());
    setRemarks("");
    setSaveOpen(true);
  };

  const handleSaveVersion = async () => {
    if (!effectiveFrom) {
      message.error("Pick when this rate takes effect.");
      return;
    }
    setSaving(true);
    try {
      const res = await equipmentRateApis.saveVersion({
        assetId,
        effectiveFrom: effectiveFrom.toISOString(),
        remarks: remarks.trim() || undefined,
        ...toFields(),
      });
      if (res?.status) {
        setSaveOpen(false);
        Swal.fire({
          icon: "success",
          title: versionCount ? "New version saved" : "Rate created",
          text: res?.data?.message || res?.message,
          timer: 2200,
          showConfirmButton: false,
        });
        onSaved();
      } else {
        Swal.fire({
          icon: "error",
          title: "Could not save",
          text: res?.data?.message || res?.message || "Please try again.",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await equipmentRateApis.getHistory(assetId);
      setHistory(res?.data?.versions || []);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleUndoLatest = async () => {
    const res = await equipmentRateApis.deleteLatestVersion(assetId);
    if (res?.status) {
      message.success(res?.data?.message || "Latest version removed.");
      setHistoryOpen(false);
      load();
    } else {
      message.error(res?.data?.message || res?.message || "Could not remove.");
    }
  };

  // ---- small render helpers ------------------------------------------------

  const cardName =
    heads?.resolvedFrom === "PROJECT"
      ? "Project card"
      : heads?.resolvedFrom === "ORG"
      ? "Organization card"
      : "rate-parameters card";

  const renderField = (field: FieldSpec, width = "col-md-4") => {
    const isBlank =
      form[field.key as string] === null || form[field.key as string] === undefined;
    const inherited =
      field.inheritable && isBlank && inheritedFields.includes(field.key as string);
    return (
      <div className={`${width} mb-3`} key={field.key as string}>
        <label className="form-label mb-1">
          {field.label}
          {field.hint ? (
            <Tooltip title={field.hint}>
              <span className="text-soft ms-1" style={{ cursor: "help" }}>
                ⓘ
              </span>
            </Tooltip>
          ) : null}
        </label>
        <InputNumber
          value={form[field.key as string]}
          onChange={(v) => set(field.key as string, v)}
          style={{ width: "100%" }}
          min={0}
          addonAfter={field.suffix}
          placeholder={inherited ? "inherited" : "—"}
        />
        {inherited ? (
          <div className="text-soft" style={{ fontSize: 11, marginTop: 2 }}>
            Blank — inherited from the {cardName}.
          </div>
        ) : field.inheritable && isBlank ? (
          <div className="text-soft" style={{ fontSize: 11, marginTop: 2 }}>
            Blank — no card default either, treated as 0.
          </div>
        ) : null}
      </div>
    );
  };

  /**
   * The workbook signature: each section closes with its derived rows and its
   * own Hourly Cost line. Greys out while inputs have changed since the last
   * server calculation.
   */
  const SectionFooter = ({
    rows,
    outputLabel = "Hourly Cost",
    output,
    perUnitOutput,
    formula,
  }: {
    rows?: Array<[string, string]>;
    outputLabel?: string;
    output: any;
    /**
     * The same head per unit of output. When the toggle is on ₹/unit and this
     * is present, it replaces `output`; when it is absent (no standard output
     * recorded) the line falls back to the hourly figure WITH its /hr label
     * still attached, so the number is never mislabelled.
     */
    perUnitOutput?: any;
    /** the head's formula, shown as an ⓘ tooltip on the output line */
    formula?: string;
  }) => (
    <div
      style={{
        borderTop: "1px dashed #cbd7e4",
        marginTop: 4,
        paddingTop: 8,
        opacity: dirtySincePreview ? 0.45 : 1,
        transition: "opacity .15s",
      }}
    >
      {(rows ?? []).map(([label, value]) => (
        <div
          key={label}
          className="d-flex justify-content-between"
          style={{ fontSize: 12.5, color: "#5d6f83", padding: "1px 0" }}
        >
          <span>{label}</span>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{value}</span>
        </div>
      ))}
      <div
        className="d-flex justify-content-between"
        style={{ fontWeight: 700, color: "#022c57", paddingTop: 3, fontSize: 13.5 }}
      >
        <span>
          {showPerUnit && perUnitOutput !== undefined
            ? outputLabel.replace("Hourly", "Per-Unit")
            : outputLabel}
          {formula ? (
            <Tooltip title={formula} overlayStyle={{ maxWidth: 420 }}>
              <span
                className="text-soft ms-1"
                style={{ cursor: "help", fontWeight: 400 }}
              >
                ⓘ
              </span>
            </Tooltip>
          ) : null}
        </span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {showPerUnit && perUnitOutput !== undefined
            ? `₹ ${moneyPerUnit(perUnitOutput)} /${outputUomLabel || "unit"}`
            : `₹ ${money(output)} /hr`}
        </span>
      </div>
    </div>
  );

  /** Read-only price-in-force line for the POL sub-blocks. */
  const PriceInForce = ({ label, value, unit }: { label: string; value: any; unit: string }) => (
    <div
      className="d-flex align-items-center mb-2"
      style={{ gap: 8, fontSize: 12.5, color: "#5d6f83", flexWrap: "wrap" }}
    >
      <span>
        {label}: <strong style={{ color: "#022c57" }}>₹ {money(value)}</strong> {unit}
      </span>
      <Tag color="default" style={{ marginInlineEnd: 0 }}>
        from the {cardName}
      </Tag>
      <a
        href="#rate-parameters"
        onClick={(e) => {
          e.preventDefault();
          setParametersOpen(true);
        }}
        style={{ fontSize: 12 }}
      >
        Edit
      </a>
    </div>
  );

  // ---- the steps: the six workbook sections --------------------------------

  const steps = useMemo(
    () => [
      {
        key: "s1",
        title: "1 · Depreciation Charges",
        railTitle: "1 · Depreciation",
        cost: heads?.depreciation,
        headKey: "depreciation",
        children: (
          <>
            <div className="row">{OWNERSHIP_FIELDS.map((f) => renderField(f, "col-md-6"))}</div>
            {/* The ₹/unit basis. Optional, and independent of everything above
                it — a machine with no standard output still rates per hour. */}
            <div className="row">
              {renderField(OUTPUT_FIELD, "col-md-6")}
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: 12 }}>
                    Output UOM
                  </label>
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    style={{ width: "100%" }}
                    placeholder="e.g. Cum"
                    value={form.outputUomId ?? undefined}
                    options={uomOptions}
                    onChange={(value) => set("outputUomId", value ?? null)}
                  />
                  <div className="text-soft" style={{ fontSize: 11, marginTop: 4 }}>
                    The numerator of “Cum/Hr” — the “/Hr” is implied.
                  </div>
                </div>
              </div>
            </div>
            <SectionFooter
              rows={[
                ["Annual scheduled production hours", qty(heads?.annualHours)],
                ...(heads?.perUnit?.hoursPerUnit
                  ? [
                      [
                        `Machine-hours per ${outputUomLabel || "unit"}`,
                        qty(heads.perUnit.hoursPerUnit),
                      ] as [string, string],
                    ]
                  : []),
              ]}
              output={heads?.depreciation}
              perUnitOutput={heads?.perUnit?.depreciation}
              formula={HEAD_FORMULAS.depreciation}
            />
          </>
        ),
      },
      {
        key: "s2",
        title: "2 · Insurance Charges",
        railTitle: "2 · Insurance",
        cost: heads?.insurance,
        headKey: "insurance",
        children: (
          <>
            <div className="row">{INSURANCE_FIELDS.map((f) => renderField(f, "col-md-6"))}</div>
            <SectionFooter
              rows={[["Insurance cost per year", `${qty(intermediates?.insuranceCostPerYearLacs)} Lacs`]]}
              output={heads?.insurance}
              perUnitOutput={heads?.perUnit?.insurance}
              formula={HEAD_FORMULAS.insurance}
            />
          </>
        ),
      },
      {
        key: "s3",
        title: "3 · Interest on Capital Investment",
        railTitle: "3 · Interest",
        cost: heads?.interest,
        headKey: "interest",
        children: (
          <>
            <div className="row">{INTEREST_FIELDS.map((f) => renderField(f, "col-md-6"))}</div>
            <SectionFooter
              rows={[
                ["Average annual cost", `${qty(intermediates?.averageAnnualCostLacs)} Lacs`],
                ["Interest cost per year", `${qty(intermediates?.interestCostPerYearLacs)} Lacs`],
              ]}
              output={heads?.interest}
              perUnitOutput={heads?.perUnit?.interest}
              formula={HEAD_FORMULAS.interest}
            />
          </>
        ),
      },
      {
        key: "s4",
        title: "4 · Repair & Maintenance",
        railTitle: "4 · Repair",
        cost: heads?.repair,
        headKey: "repair",
        children: (
          <>
            <div className="row">{REPAIR_FIELDS.map((f) => renderField(f, "col-md-6"))}</div>
            <SectionFooter
              output={heads?.repair}
              perUnitOutput={heads?.perUnit?.repair}
              formula={HEAD_FORMULAS.repair}
            />
          </>
        ),
      },
      {
        key: "s5",
        title: "5 · POL Charges",
        railTitle: "5 · POL",
        cost: intermediates?.totalPol,
        headKey: "totalPol",
        children: (
          <>
            <div className="mb-1" style={{ fontWeight: 600, color: "#2c4a6d", fontSize: 12.5 }}>
              DIESEL OPERATED
            </div>
            <PriceInForce
              label="Diesel price in force"
              value={intermediates?.dieselPriceUsed}
              unit="/ litre"
            />
            <div className="row">{DIESEL_FIELDS.map((f) => renderField(f))}</div>
            <div style={{ fontSize: 12.5, color: "#5d6f83" }}>
              <div>
                Consumption {qty(intermediates?.dieselLitresPerHour)} lt/hr → effective{" "}
                {qty(intermediates?.effectiveDieselLitresPerHour)} lt/hr
              </div>
              <div style={{ fontVariantNumeric: "tabular-nums" }}>
                Fuel ₹ {money(heads?.fuel)} · Lubricant ₹ {money(intermediates?.dieselLubricants)}{" "}
                · Subtotal ₹ {money(intermediates?.fuelAndLubSubtotal)}
              </div>
            </div>

            <div
              className="mb-1 mt-3"
              style={{ fontWeight: 600, color: "#2c4a6d", fontSize: 12.5 }}
            >
              ELECTRIC OPERATED
            </div>
            <PriceInForce
              label="Electricity rate in force"
              value={intermediates?.electricityRateUsed}
              unit="/ kWh"
            />
            <div className="row">{ELECTRIC_FIELDS.map((f) => renderField(f))}</div>
            <div style={{ fontSize: 12.5, color: "#5d6f83" }}>
              <div>Consumption {qty(intermediates?.kwhPerHour)} kWh/hr</div>
              <div style={{ fontVariantNumeric: "tabular-nums" }}>
                Electricity ₹ {money(heads?.electricity)} · Lubricant ₹{" "}
                {money(intermediates?.elecLubricants)} · Subtotal ₹{" "}
                {money(intermediates?.powerAndLubSubtotal)}
              </div>
            </div>

            <div
              className="mb-1 mt-3"
              style={{ fontWeight: 600, color: "#2c4a6d", fontSize: 12.5 }}
            >
              SUNDRIES
            </div>
            <div className="row">{SUNDRIES_FIELDS.map((f) => renderField(f, "col-md-6"))}</div>
            {/* The workbook's closing chain, row for row: Sub Total Fuel & Lubs
                (5.12) + Sub Total Power & Lubs (5.13) = Total POL Charges, then
                + Sundries & Misc (5.15) = Total POL Cost (5.16). */}
            <SectionFooter
              rows={[
                ["Sub Total Fuel & Lubs", `₹ ${money(intermediates?.fuelAndLubSubtotal)} /hr`],
                ["Sub Total Power & Lubs", `₹ ${money(intermediates?.powerAndLubSubtotal)} /hr`],
                [
                  "Total POL Charges",
                  `₹ ${money(
                    (Number(intermediates?.fuelAndLubSubtotal) || 0) +
                      (Number(intermediates?.powerAndLubSubtotal) || 0),
                  )} /hr`,
                ],
                ["Sundries & Misc", `₹ ${money(heads?.misc)} /hr`],
              ]}
              outputLabel="Total POL Cost"
              output={intermediates?.totalPol}
              perUnitOutput={polPerUnit}
              formula={`${HEAD_FORMULAS.totalPol}. ${HEAD_FORMULAS.fuel}. ${HEAD_FORMULAS.electricity}. ${HEAD_FORMULAS.lubricants}. ${HEAD_FORMULAS.misc}`}
            />
            {/* The one deliberate deviation from the source workbook, stated
                where the comparison happens — otherwise every side-by-side
                check "finds" this as a bug. */}
            <div className="text-soft mt-2" style={{ fontSize: 11.5 }}>
              Lubricants are priced on the <strong>effective</strong> (actual)
              energy cost. The source workbook prices them on the gross cost
              before effective usage, so its POL total reads higher for any
              machine running below 100% effective usage — that difference is a
              deliberate correction, not a calculation error.
            </div>
          </>
        ),
      },
      {
        key: "s6",
        title: "6 · Manpower",
        railTitle: "6 · Manpower",
        cost: heads?.crew,
        headKey: "crew",
        children: (
          <>
            <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap">
              <div className="row" style={{ minWidth: 220 }}>
                {CREW_ENVELOPE_FIELDS.map((f) => renderField(f, "col-12"))}
              </div>
              <a
                href="#rate-parameters"
                onClick={(e) => {
                  e.preventDefault();
                  setParametersOpen(true);
                }}
                style={{ fontSize: 12 }}
              >
                Edit wages in Rate Parameters
              </a>
            </div>

            {/* The workbook's wages + requirement + monthly-charges block as one
                table. Wages are READ-ONLY here — they live on the shared card so
                one edit re-rates the fleet. */}
            <div style={{ overflowX: "auto" }}>
              <table className="w-100" style={{ fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ color: "#5d6f83", fontSize: 12 }}>
                    <th style={{ textAlign: "left", padding: "4px 6px" }}>Role</th>
                    <th style={{ textAlign: "right", padding: "4px 6px" }}>Wage ₹/month</th>
                    <th style={{ textAlign: "right", padding: "4px 6px", width: 140 }}>
                      Nos/Shift
                    </th>
                    <th style={{ textAlign: "right", padding: "4px 6px" }}>Monthly ₹</th>
                  </tr>
                </thead>
                <tbody>
                  {CREW_ROLES.map((role) => {
                    const line = crewByRole.get(role.roleKey);
                    return (
                      <tr key={role.roleKey} style={{ borderTop: "1px solid #eef2f7" }}>
                        <td style={{ padding: "3px 6px" }}>
                          {role.label}{" "}
                          <Tag
                            color={role.skill === "Skilled" ? "blue" : "default"}
                            style={{ fontSize: 10, lineHeight: "16px", marginInlineEnd: 0 }}
                          >
                            {role.skill}
                          </Tag>
                        </td>
                        <td
                          style={{
                            padding: "3px 6px",
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                            color: "#5d6f83",
                            opacity: dirtySincePreview ? 0.45 : 1,
                          }}
                        >
                          {money(line?.monthlyWage)}
                        </td>
                        <td style={{ padding: "3px 6px", textAlign: "right" }}>
                          <InputNumber
                            size="small"
                            value={form[role.field as string]}
                            onChange={(v) => set(role.field as string, v)}
                            min={0}
                            style={{ width: 120 }}
                            placeholder="0"
                          />
                        </td>
                        <td
                          style={{
                            padding: "3px 6px",
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                            opacity: dirtySincePreview ? 0.45 : 1,
                          }}
                        >
                          {money(line?.monthlyCost)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="row mt-3">{INDIRECT_FIELDS.map((f) => renderField(f, "col-md-6"))}</div>
            <SectionFooter
              rows={[
                [
                  "Skilled — monthly / hourly",
                  `₹ ${money(intermediates?.skilledMonthly)} · ₹ ${money(
                    intermediates?.skilledHourly,
                  )}/hr`,
                ],
                [
                  "Unskilled — monthly / hourly",
                  `₹ ${money(intermediates?.unskilledMonthly)} · ₹ ${money(
                    intermediates?.unskilledHourly,
                  )}/hr`,
                ],
              ]}
              output={heads?.crew}
              perUnitOutput={heads?.perUnit?.crew}
              formula={HEAD_FORMULAS.crew}
            />
          </>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form, heads, intermediates, crewByRole, inheritedFields, dirtySincePreview],
  );

  const isLastStep = activeStep === steps.length - 1;
  const isRetired = asset?.status === "Retired";
  const saveLabel = versionCount ? "Save as New Version" : "Create First Version";

  const historyColumns: any[] = [
    {
      title: "Effective From",
      dataIndex: "effectiveFrom",
      key: "effectiveFrom",
      width: 150,
      render: (v: string, _r: any, index: number) => (
        <div>
          <div>{v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"}</div>
          {index === 0 ? (
            <Tag color="green" style={{ fontSize: 10, lineHeight: "16px" }}>
              Latest
            </Tag>
          ) : null}
        </div>
      ),
    },
    {
      title: "Total ₹/hr",
      dataIndex: "totalPerHour",
      key: "totalPerHour",
      align: "right" as const,
      width: 110,
      // Each history row is priced by the parameter card in force AT ITS OWN
      // instant — what the machine actually cost then, not today's prices.
      render: (v: any) => <span style={{ fontVariantNumeric: "tabular-nums" }}>{money(v)}</span>,
    },
    {
      // Its own column rather than a toggled one: each version carries its OWN
      // standard output, so a version that changed only the output basis shows
      // a different ₹/unit against an unchanged ₹/hr — the point of the row.
      title: "Total ₹/Unit",
      dataIndex: "totalPerUnit",
      key: "totalPerUnit",
      align: "right" as const,
      width: 120,
      render: (v: any, row: any) => (
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          {v === undefined || v === null ? (
            <span className="text-soft">—</span>
          ) : (
            <>
              {moneyPerUnit(v)}
              {row?.outputUomName ? (
                <span className="text-soft" style={{ fontSize: 11 }}>
                  {" "}
                  /{row.outputUomName}
                </span>
              ) : null}
            </>
          )}
        </span>
      ),
    },
    {
      title: "Cost (Lacs)",
      dataIndex: "costOfEquipmentLacs",
      key: "cost",
      align: "right" as const,
      width: 95,
      render: (v: any) => qty(v),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      ellipsis: true,
      render: (v: string) => v || "—",
    },
    {
      title: "By",
      dataIndex: "createdByUserName",
      key: "by",
      width: 90,
      render: (v: string) => v || "—",
    },
    {
      title: "",
      key: "undo",
      width: 70,
      // Only the LATEST version can be removed — older history is immutable.
      render: (_: any, _r: any, index: number) =>
        index === 0 ? (
          <Popconfirm
            title="Remove the latest version?"
            description="The previous version comes back into force. Older history cannot be deleted."
            onConfirm={handleUndoLatest}
            okText="Remove"
            cancelText="Keep"
          >
            <a href="#undo" onClick={(e) => e.preventDefault()}>
              Undo
            </a>
          </Popconfirm>
        ) : null,
    },
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spin />
      </div>
    );
  }

  return (
    <div className="card-inner card-inner-lg">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: 8 }}>
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
          <h5 className="mb-0" style={{ color: "#022c57", fontWeight: 600 }}>
            {asset?.assetName || "Equipment"}
          </h5>
          {/* Identity comes from the Asset Master and is read-only here. */}
          <Tag>{asset?.assetCode}</Tag>
          {asset?.capacity ? <Tag color="default">{asset.capacity}</Tag> : null}
          <Tag color={statusTagColor(asset?.status)}>{statusLabel(asset?.status)}</Tag>
          <Tag color={projectId > 0 ? "blue" : "default"}>
            {projectId > 0
              ? projectName || `Project ${projectId}`
              : "Organization (Master)"}
          </Tag>
          {baseVersion ? (
            <span className="text-soft" style={{ fontSize: 12 }}>
              Rate in force since{" "}
              {dayjs(baseVersion.effectiveFrom).format("DD MMM YYYY HH:mm")}
            </span>
          ) : (
            <Tag color="gold">No rate yet</Tag>
          )}
        </div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          {versionCount > 0 && (
            <Button color="light" size="sm" className="btn-white" onClick={openHistory}>
              <HistoryOutlined style={{ marginRight: 4 }} />
              <span>
                History ({versionCount})
              </span>
            </Button>
          )}
          <Button color="light" size="sm" onClick={onClose}>
            <Icon name="arrow-left" />
            <span>Back</span>
          </Button>
        </div>
      </div>

      {isRetired && (
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message="This machine is retired"
          description="Retired machines keep their price history for old reports but cannot take new rate versions. Reactivate it in the Asset Master first."
        />
      )}

      <div className="row">
        {/* ---- left rail: the workbook's section skeleton as a stepper ---- */}
        <div className="col-lg-2 mb-3">
          <div className="equipment-rate-rail">
            <Steps
              className="equipment-rate-steps"
              direction="vertical"
              size="small"
              current={activeStep}
              onChange={(step) => setActiveStep(step)}
              items={steps.map((step, i) => ({
                // Short, single-line titles: the active step goes bold, and a
                // long title re-wrapping under bold width made the rail jump.
                title: step.railTitle,
                // Free navigation, so no fake "completed" ticks — the only
                // states are the section you're on and the ones you're not.
                status: i === activeStep ? "process" : "wait",
                description: (() => {
                  const value = showPerUnit
                    ? railPerUnit(step.headKey)
                    : step.cost;
                  if (value === null || value === undefined) return undefined;
                  return (
                    <span style={{ opacity: dirtySincePreview ? 0.45 : 1 }}>
                      {showPerUnit
                        ? `₹ ${moneyPerUnit(value)} /${outputUomLabel || "unit"}`
                        : `₹ ${money(value)} /hr`}
                    </span>
                  );
                })(),
              }))}
            />
            {/* The rail ends the way the sheet's column does — in the total. */}
            <div
              className="d-flex justify-content-between"
              style={{
                borderTop: "1.5px solid #022c57",
                marginTop: 8,
                paddingTop: 8,
                paddingRight: 8,
                fontWeight: 700,
                color: "#022c57",
                fontVariantNumeric: "tabular-nums",
                opacity: dirtySincePreview ? 0.45 : 1,
              }}
            >
              <span>Grand Total</span>
              <span>
                {showPerUnit
                  ? `₹ ${moneyPerUnit(heads?.perUnit?.total)} /${
                      outputUomLabel || "unit"
                    }`
                  : `₹ ${money(heads?.total)} /hr`}
              </span>
            </div>
          </div>
        </div>

        {/* ---- active section ---- */}
        <div className="col-lg-7 mb-3">
          <div className="equipment-rate-step-card" ref={stepCardRef}>
            <h6 className="mb-3" style={{ color: "#022c57" }}>
              {steps[activeStep].title}
            </h6>
            {steps[activeStep].children}

            <div
              className="d-flex justify-content-between"
              style={{ marginTop: 20, paddingTop: 12, borderTop: "1px solid #e4e9f0" }}
            >
              <Button
                color="light"
                size="sm"
                disabled={activeStep === 0}
                onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
              >
                <Icon name="arrow-left" />
                <span>Previous</span>
              </Button>
              {!isLastStep ? (
                <Button
                  color="light"
                  size="sm"
                  className="btn-white"
                  onClick={() => setActiveStep((s) => Math.min(steps.length - 1, s + 1))}
                >
                  <span>Next</span>
                  <Icon name="arrow-right" />
                </Button>
              ) : (
                <Button
                  color="primary"
                  size="sm"
                  disabled={saving || isRetired}
                  onClick={openSaveModal}
                >
                  {saveLabel}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* ---- Summary — the workbook's recap block (its rows 111-122) ---- */}
        <div className="col-lg-3">
          <div className="equipment-rate-summary">
          <div
            style={{
              border: "1px solid #dbe3ec",
              borderRadius: 4,
              padding: 16,
              background: "#f7f9fc",
            }}
          >
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0" style={{ color: "#022c57" }}>
                Summary
              </h6>
              {/* Disabled until this machine HAS an output basis — switching
                  would otherwise show a column of em dashes with no clue why. */}
              <RateUnitToggle
                uomLabel={outputUomLabel}
                disabled={!heads?.perUnit}
                disabledReason="Set a Standard Output per Hour in section 1 and recalculate to cost this machine per unit of output."
              />
            </div>

            {dirtySincePreview && heads ? (
              <Alert
                type="warning"
                showIcon
                className="mb-2"
                message="Inputs changed — recalculate to refresh"
              />
            ) : null}

            {!heads ? (
              <div className="text-soft" style={{ fontSize: 13 }}>
                Enter the inputs and press Recalculate to see this machine's
                hourly cost. Every number — section by section — is computed from
                the inputs plus the scope's rate parameters; nothing is typed in.
              </div>
            ) : (
              <>
                <table
                  className="w-100"
                  style={{ fontSize: 13, opacity: dirtySincePreview ? 0.45 : 1 }}
                >
                  <tbody>
                    {[
                      { label: "1 · Depreciation", value: summaryValue("depreciation", heads.depreciation), formula: summaryFormula("depreciation") },
                      { label: "2 · Insurance", value: summaryValue("insurance", heads.insurance), formula: summaryFormula("insurance") },
                      { label: "3 · Interest", value: summaryValue("interest", heads.interest), formula: summaryFormula("interest") },
                      { label: "4 · Repair", value: summaryValue("repair", heads.repair), formula: summaryFormula("repair") },
                      // POL is a workbook SUBTOTAL, not an engine head, so it has
                      // no perUnit sibling — sum its four constituents instead,
                      // which is exactly what the hourly figure is.
                      { label: "5 · POL", value: showPerUnit ? polPerUnit : intermediates?.totalPol, formula: summaryFormula("totalPol") },
                      { label: "Fuel", value: summaryValue("fuel", heads.fuel), formula: summaryFormula("fuel"), sub: true },
                      { label: "Electricity", value: summaryValue("electricity", heads.electricity), formula: summaryFormula("electricity"), sub: true },
                      { label: "Lubricants", value: summaryValue("lubricants", heads.lubricants), formula: summaryFormula("lubricants"), sub: true },
                      { label: "Sundries & Misc", value: summaryValue("misc", heads.misc), formula: summaryFormula("misc"), sub: true },
                      { label: "6 · Manpower (Crew)", value: summaryValue("crew", heads.crew), formula: summaryFormula("crew") },
                    ].map((row) => (
                      <tr key={row.label}>
                        <td
                          style={{
                            padding: row.sub ? "1px 0 1px 16px" : "3px 0",
                            color: row.sub ? "#8091a7" : "#5d6f83",
                            fontSize: row.sub ? 12 : 13,
                          }}
                        >
                          {/* hover a line for its formula */}
                          <Tooltip title={row.formula} overlayStyle={{ maxWidth: 420 }}>
                            <span
                              style={{
                                cursor: "help",
                                textDecoration: "underline dotted #b6c4d4",
                                textUnderlineOffset: 3,
                              }}
                            >
                              {row.sub ? `· ${row.label}` : row.label}
                            </span>
                          </Tooltip>
                        </td>
                        <td
                          style={{
                            padding: row.sub ? "1px 0" : "3px 0",
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                            color: row.sub ? "#8091a7" : undefined,
                            fontSize: row.sub ? 12 : 13,
                          }}
                        >
                          {showPerUnit ? moneyPerUnit(row.value) : money(row.value)}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td
                        style={{
                          padding: "8px 0 3px",
                          borderTop: "1.5px solid #022c57",
                          fontWeight: 700,
                        }}
                      >
                        <Tooltip
                          title={summaryFormula("total")}
                          overlayStyle={{ maxWidth: 420 }}
                        >
                          <span
                            style={{
                              cursor: "help",
                              textDecoration: "underline dotted #b6c4d4",
                              textUnderlineOffset: 3,
                            }}
                          >
                            Total{" "}
                            {showPerUnit
                              ? `₹/${outputUomLabel || "unit"}`
                              : "₹/hr"}
                          </span>
                        </Tooltip>
                      </td>
                      <td
                        style={{
                          padding: "8px 0 3px",
                          borderTop: "1.5px solid #022c57",
                          textAlign: "right",
                          fontWeight: 700,
                          fontVariantNumeric: "tabular-nums",
                          color: "#022c57",
                        }}
                      >
                        {showPerUnit
                          ? moneyPerUnit(heads.perUnit?.total)
                          : money(heads.total)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {heads.annualHours ? (
                  <div className="text-soft mt-2" style={{ fontSize: 11 }}>
                    Based on {money(heads.annualHours)} annual hours
                    {heads.resolvedFrom === "PROJECT"
                      ? ", this project's rate parameters"
                      : heads.resolvedFrom === "ORG"
                      ? ", the organization rate parameters"
                      : ""}
                    .
                  </div>
                ) : null}

                {heads.warnings?.length ? (
                  <Alert
                    type="warning"
                    showIcon
                    className="mt-2"
                    message={
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                        {heads.warnings.map((w, i) => (
                          <li key={i}>{warningLabel(w)}</li>
                        ))}
                      </ul>
                    }
                  />
                ) : null}
              </>
            )}

            {/* Below the figures, per user preference: read the summary, then
                refresh it. Full width so it reads as the panel's action. */}
            <Button
              color="light"
              className="btn-block w-100 justify-content-center mt-3"
              disabled={previewing}
              onClick={handleRecalculate}
            >
              {previewing ? "Calculating…" : "Recalculate"}
            </Button>
          </div>

          {/* The primary action lives under the summary — read the figures,
              then commit them. (The last step's button remains for the guided
              flow; both open the same save-as-version modal.) */}
          <Button
            color="primary"
            className="btn-block w-100 justify-content-center mt-2"
            disabled={saving || isRetired}
            onClick={openSaveModal}
          >
            {saveLabel}
          </Button>
          </div>
        </div>
      </div>

      {/* ---- Save = INSERT a version: when it takes effect, and why ---- */}
      <Modal
        title={saveLabel}
        open={saveOpen}
        onCancel={() => setSaveOpen(false)}
        onOk={handleSaveVersion}
        okText={saving ? "Saving…" : "Save Version"}
        okButtonProps={{ disabled: saving }}
        destroyOnClose
      >
        <div className="text-soft mb-3" style={{ fontSize: 12.5 }}>
          {versionCount
            ? "Saving adds a NEW version of this machine's rate — the current one is kept and stays in force for everything before the instant you pick. Back-dating recalculates history from that date."
            : "This becomes the machine's first rate version, in force from the instant you pick."}
        </div>
        <div className="mb-3">
          <label className="form-label mb-1">Effective from</label>
          <DatePicker
            value={effectiveFrom}
            onChange={(v) => setEffectiveFrom(v as Dayjs)}
            showTime={{ format: "HH:mm" }}
            format="DD MMM YYYY HH:mm"
            style={{ width: "100%" }}
            allowClear={false}
          />
        </div>
        <div>
          <label className="form-label mb-1">Remarks</label>
          <Input
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Why the rate changed (e.g. overhaul, revised life)"
            maxLength={500}
          />
        </div>
      </Modal>

      {/* ---- Rate History: the timeline, each row priced as-of its own instant ---- */}
      <Drawer
        title={`Rate History — ${asset?.assetName || ""}`}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        width={720}
      >
        {historyLoading ? (
          <div className="text-center py-5">
            <Spin />
          </div>
        ) : (
          <>
            <div className="text-soft mb-2" style={{ fontSize: 12 }}>
              Each version shows the total it produced with the rate-parameter
              card in force at ITS effective instant — the machine's real price
              on that date, not today's prices on old inputs.
            </div>
            <Table
              rowKey="equipmentRateId"
              size="small"
              columns={historyColumns}
              dataSource={history}
              pagination={false}
            />
          </>
        )}
      </Drawer>

      <RateParametersDrawer
        open={parametersOpen}
        projectId={projectId}
        projectName={projectName}
        onClose={() => setParametersOpen(false)}
        // Saved prices change every derived number — recalculate immediately so
        // the sections never show figures from the old card.
        onSaved={handleRecalculate}
      />
    </div>
  );
};

export default EquipmentRateEditor;
